import React, { useRef } from 'react';
import { exportElementToPDF } from '@/utils/pdf';
import { Printer, ShieldCheck, X } from 'lucide-react';
import { useHospitalConfig } from '../../hooks/useSystemSettings';
import { useThemeStore } from '../../stores/useThemeStore';

/**
 * Patient-facing disclosure card.
 *
 * Bilingual (EN + AR) so the doctor can show or print one document
 * regardless of the patient's preferred language. Designed to print
 * cleanly on A4 / Letter — printed copy is the legal artifact, the
 * on-screen view is the same content.
 *
 * **The copy below is a reasonable starting draft and MUST be reviewed
 * by counsel before launch.** Keep wording symmetric across the two
 * columns when amending so the printed page stays balanced.
 */

interface Props {
  retentionDays?: number;
  /**
   * Clinic name. Single string used for both columns when the hospital
   * config has no Arabic localization, OR an explicit { en, ar } pair
   * for proper bilingual display.
   */
  clinicName?: string | { en?: string; ar?: string };
  contactEmail?: string;
  contactPhone?: string;
}

const pickName = (
  name: Props['clinicName'],
  lang: 'en' | 'ar',
): string | undefined => {
  if (!name) return undefined;
  if (typeof name === 'string') return name;
  return name[lang] || name.en || name.ar;
};

export const PatientDisclosureCard: React.FC<Props> = ({
  retentionDays = 60,
  clinicName,
  contactEmail,
  contactPhone,
}) => {
  const enName = pickName(clinicName, 'en');
  const arName = pickName(clinicName, 'ar');
  return (
    <article
      className="bg-white text-black p-8 sm:p-10 print:p-6"
      style={{ fontFamily: 'system-ui, sans-serif', lineHeight: 1.55 }}
    >
      <header
        className="text-center mb-8"
        style={{ breakInside: 'avoid', breakAfter: 'avoid' }}
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-black/20 text-[10px] uppercase tracking-[0.25em] font-semibold">
          <ShieldCheck className="w-3.5 h-3.5" />
          Patient privacy notice
        </div>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight">
          Recording &amp; AI improvement
        </h1>
        {(enName || arName) && (
          <p className="mt-1 text-sm text-black/60">
            {enName || arName}
            {enName && arName && enName !== arName ? ` · ${arName}` : ''}
          </p>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 print:grid-cols-1 print:gap-0">
        {/* English column. On screen: side-by-side with the Arabic
            column below md. On print: full page; the Arabic section
            below forces a page break so each language reads as its
            own one-language handout. */}
        <section
          className="space-y-4 text-sm"
          lang="en"
          dir="ltr"
          style={{ breakInside: 'avoid' }}
        >
          <Heading>About this recording</Heading>
          <p>
            Your consultation today may be recorded as audio and converted
            to text by an AI tool, so your doctor can focus on you instead
            of typing notes. The transcript is added to your medical
            record.
          </p>

          <Heading>Why we keep the audio</Heading>
          <p>
            With your permission, we keep the encrypted audio for up to{' '}
            <strong>{retentionDays} days</strong> and use it to make the
            transcription model more accurate over time. We do not sell
            it. We do not share it with advertisers.
          </p>

          <Heading>How it is protected</Heading>
          <ul className="list-disc list-inside space-y-1">
            <li>Encrypted with a key only your clinic holds.</li>
            <li>Stored on infrastructure inside your country.</li>
            <li>Never sent to third-party AI providers.</li>
          </ul>

          <Heading>Your rights</Heading>
          <ul className="list-disc list-inside space-y-1">
            <li>
              You may decline. Your care will not be affected if you say
              no.
            </li>
            <li>
              You may ask us to delete the recording at any time, even
              after this visit.
            </li>
            <li>You may ask to see what we have stored about you.</li>
          </ul>

          {(contactEmail || contactPhone) && (
            <p className="text-xs text-black/60">
              Questions or requests:{' '}
              {contactEmail && (
                <span className="font-mono">{contactEmail}</span>
              )}
              {contactEmail && contactPhone && ' · '}
              {contactPhone && (
                <span className="font-mono">{contactPhone}</span>
              )}
            </p>
          )}
        </section>

        {/* Arabic column. `breakBefore: page` only affects the print
            stylesheet — on screen it's a no-op (still side-by-side). */}
        <section
          className="space-y-4 text-sm print:pt-8"
          lang="ar"
          dir="rtl"
          style={{
            fontFamily: 'system-ui, "Tahoma", sans-serif',
            breakBefore: 'page',
            breakInside: 'avoid',
          }}
        >
          <Heading rtl>عن هذا التسجيل</Heading>
          <p>
            قد يتم تسجيل صوت زيارتك اليوم وتحويله إلى نص بواسطة أداة ذكاء
            اصطناعي، حتى يتمكن طبيبك من التركيز عليك بدلاً من كتابة
            الملاحظات. يتم إضافة النص إلى ملفك الطبي.
          </p>

          <Heading rtl>لماذا نحتفظ بالتسجيل</Heading>
          <p>
            بموافقتك، نحتفظ بالتسجيل الصوتي مشفّراً لمدة تصل إلى{' '}
            <strong>{retentionDays} يوماً</strong> ونستخدمه لتحسين دقة
            النموذج بمرور الوقت. لا نبيعه. ولا نشاركه مع المعلنين.
          </p>

          <Heading rtl>كيف نحميه</Heading>
          <ul className="list-disc list-inside space-y-1">
            <li>مشفّر بمفتاح لا تملكه إلا عيادتك.</li>
            <li>مخزّن على بنية تحتية داخل بلدك.</li>
            <li>لا يُرسل أبداً إلى مزوّدي ذكاء اصطناعي خارجيين.</li>
          </ul>

          <Heading rtl>حقوقك</Heading>
          <ul className="list-disc list-inside space-y-1">
            <li>يمكنك الرفض. لن تتأثر رعايتك إذا قلت لا.</li>
            <li>يمكنك طلب حذف التسجيل في أي وقت، حتى بعد هذه الزيارة.</li>
            <li>يمكنك طلب الاطلاع على ما هو مخزّن عنك.</li>
          </ul>

          {(contactEmail || contactPhone) && (
            <p className="text-xs text-black/60">
              للاستفسار أو الطلبات:{' '}
              {contactEmail && (
                <span className="font-mono" dir="ltr">
                  {contactEmail}
                </span>
              )}
              {contactEmail && contactPhone && ' · '}
              {contactPhone && (
                <span className="font-mono" dir="ltr">
                  {contactPhone}
                </span>
              )}
            </p>
          )}
        </section>
      </div>

      <footer className="mt-10 pt-4 border-t border-black/10 text-[10px] uppercase tracking-[0.18em] text-black/50 text-center">
        Generated for in-clinic display · Not a contract
      </footer>
    </article>
  );
};

const Heading: React.FC<{ children: React.ReactNode; rtl?: boolean }> = ({
  children,
  rtl,
}) => (
  <h2
    className="text-[10px] uppercase tracking-[0.25em] font-semibold pb-1 border-b border-black/15"
    style={{ textAlign: rtl ? 'right' : 'left' }}
  >
    {children}
  </h2>
);

interface ModalProps {
  open: boolean;
  onClose: () => void;
  retentionDays?: number;
}

/**
 * Full-screen modal wrapper. Click outside or ✕ to close. The Print button
 * renders the card to a PDF (html2canvas → jsPDF) and downloads + auto-prints
 * the PDF, so it never prints the app shell.
 */
export const PatientDisclosureModal: React.FC<ModalProps> = ({
  open,
  onClose,
  retentionDays,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  // Authoritative source for clinic identity + contact info. Hospital
  // config is a public endpoint (no auth required) and already cached
  // for 5min by the shared hook, so opening this modal doesn't trigger
  // a fresh round-trip in the common case.
  const { data: hospitalConfigResponse } = useHospitalConfig();
  // Theme branding is the fallback for tenants whose hospital config
  // hasn't been filled in yet — keeps the card from looking blank.
  const branding = useThemeStore((s) => s.branding);

  // Branded-free but faithful: capture the rendered disclosure card to a PDF
  // (download + auto-print the PDF), instead of react-to-print's DOM print.
  const handlePrint = () => {
    void exportElementToPDF('patient-disclosure-card', 'patient-disclosure.pdf');
  };

  if (!open) return null;

  const config = hospitalConfigResponse?.data;
  const clinicName: string | { en?: string; ar?: string } | undefined =
    config?.name
      ? { en: config.name.en, ar: config.name.ar }
      : branding?.hospitalName || branding?.aiProvider || undefined;
  const contactEmail = config?.contact?.email || undefined;
  const contactPhone = config?.contact?.phone || undefined;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[210] flex items-end sm:items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl shadow-xl"
        style={{ backgroundColor: '#ffffff' }}
      >
        <header
          className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b sticky top-0 bg-white z-10"
          style={{ borderColor: 'rgba(0,0,0,0.08)' }}
        >
          <span
            className="text-[10px] uppercase tracking-[0.25em] font-semibold"
            style={{ color: 'rgba(0,0,0,0.55)' }}
          >
            Patient disclosure
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handlePrint?.()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-black/15 text-black hover:bg-black/5"
            >
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-black/5"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-black/60" />
            </button>
          </div>
        </header>
        <div ref={cardRef} id="patient-disclosure-card">
          <PatientDisclosureCard
            retentionDays={retentionDays}
            clinicName={clinicName}
            contactEmail={contactEmail}
            contactPhone={contactPhone}
          />
        </div>
      </div>
    </div>
  );
};
